import { computed, type Ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import {
  GET_GUARDIAN_CUSTOM_FIELD_VALUES,
  GET_STAFF_CUSTOM_FIELD_VALUES,
  GET_STUDENT_CUSTOM_FIELD_VALUES,
} from '@/graphql/customFields';
import { CustomFieldEntity } from '@/types/composition-functions';
import type {
  CustomFieldValueFieldsFragment,
  GuardianCustomFieldValuesQuery,
  StaffCustomFieldValuesQuery,
  StudentCustomFieldValuesQuery,
} from '@/types/operations';

type ValuesResult = StudentCustomFieldValuesQuery | StaffCustomFieldValuesQuery | GuardianCustomFieldValuesQuery;

const DOCUMENTS = {
  [CustomFieldEntity.Student]: GET_STUDENT_CUSTOM_FIELD_VALUES,
  [CustomFieldEntity.Staff]: GET_STAFF_CUSTOM_FIELD_VALUES,
  [CustomFieldEntity.Guardian]: GET_GUARDIAN_CUSTOM_FIELD_VALUES,
};

function pickValues(entity: CustomFieldEntity, result: ValuesResult | undefined): CustomFieldValueFieldsFragment[] {
  if (!result) return [];
  if (entity === CustomFieldEntity.Staff) return (result as StaffCustomFieldValuesQuery).staff?.customFieldValues ?? [];
  if (entity === CustomFieldEntity.Guardian) return (result as GuardianCustomFieldValuesQuery).guardian?.customFieldValues ?? [];
  return (result as StudentCustomFieldValuesQuery).student?.customFieldValues ?? [];
}

/**
 * One record's custom field values (student, staff member or guardian), in
 * the school's display order (#574). The entity is fixed for a component's
 * lifetime, so one document is chosen up front.
 */
export function useEntityCustomFieldValues(
  entity: CustomFieldEntity,
  entityId: Ref<string | number | null | undefined>,
  options: { enabled?: Ref<boolean> } = {},
) {
  const { result, loading, error, refetch } = useQuery<ValuesResult, { id: string }>(
    DOCUMENTS[entity],
    () => ({ id: String(entityId.value ?? '') }),
    () => ({
      fetchPolicy: 'cache-and-network',
      // A form for a record that does not exist yet has no id to load.
      enabled: (options.enabled?.value ?? true) && entityId.value !== null && entityId.value !== undefined && entityId.value !== '',
    }),
  );

  return {
    values: computed(() => pickValues(entity, result.value)),
    loading,
    error,
    refetch,
  };
}
